/**
 * Spec 0052 — per-tenant generic integration-settings (pass-through) management
 * (saas-auth SASATH-0067).
 *
 * Five SUPER_ADMIN-gated methods on saas-auth's tenant-scoped
 * `/api/saas-admin/tenants/{tenantId}/integration-settings` surface: list groups,
 * read one group, upsert a group, delete a key, delete a group. saas-admin stores
 * nothing — it calls saas-auth and renders masked metadata. This is the generic
 * pass-through store; it is SEPARATE from the native GHL screen/endpoints (spec
 * 0045) and must not be conflated with them.
 *
 * ENVELOPE: every response is the standard `ApiResponse<T>` envelope (like every
 * other `api/admin/*` module), so this uses the strict `request<ApiResponse<T>>()`
 * + `unwrap()` pattern of `ghlIntegration.ts` — NOT tolerant/dual-shape parsing.
 *
 * SECRET HYGIENE (mirrors the GHL token): a secret key's value is write-only — it
 * is never returned by the reads (only `valueSet`/`valueSuffix`), never logged,
 * and only ever sent on `upsertGroup` when the admin types a new value. The caller
 * OMITS the entry's `value` otherwise; the backend then preserves the stored
 * secret (merge-on-absent). Clearing a secret is `deleteKey`, never a blank upsert.
 *
 * CONTRACT STATUS — aligned to the SHIPPED SASATH-0067 wire (spec-0053 hotfix):
 * read group's key array is `keys`, the upsert request array is `entries`, and
 * the secret discriminant stays `isSecret`. See the `IntegrationGroup` /
 * `UpsertIntegrationGroupRequest` notes in `types.ts` for the confirmed shape.
 */
import { request, unwrap } from '../client';
import type {
  ApiResponse,
  IntegrationGroup,
  UpsertIntegrationGroupRequest,
} from '../types';

const base = (tenantId: number) =>
  `/api/saas-admin/tenants/${tenantId}/integration-settings`;

export const integrationSettingsApi = {
  /** List every integration-settings group for the tenant (secret values masked). */
  async listGroups(tenantId: number): Promise<IntegrationGroup[]> {
    const res = await request<ApiResponse<IntegrationGroup[]>>('GET', base(tenantId));
    return unwrap(res);
  },

  /** Read one group by name (secret values masked). */
  async getGroup(tenantId: number, group: string): Promise<IntegrationGroup> {
    const res = await request<ApiResponse<IntegrationGroup>>(
      'GET',
      `${base(tenantId)}/${group}`,
    );
    return unwrap(res);
  },

  /**
   * Create or update a group (upsert, additive merge). The caller must OMIT a
   * secret entry's `value` when the admin left the field blank — the backend
   * preserves the stored secret on an absent value (merge-on-absent); send the
   * `value` only when the admin typed a replacement. Returns the masked group.
   */
  async upsertGroup(
    tenantId: number,
    group: string,
    req: UpsertIntegrationGroupRequest,
  ): Promise<IntegrationGroup> {
    const res = await request<ApiResponse<IntegrationGroup>>(
      'PUT',
      `${base(tenantId)}/${group}`,
      { json: req },
    );
    return unwrap(res);
  },

  /** Delete a single key from a group — the way a secret is cleared. */
  async deleteKey(tenantId: number, group: string, key: string): Promise<void> {
    await request<unknown>('DELETE', `${base(tenantId)}/${group}/${key}`);
  },

  /** Delete an entire group and all of its keys. */
  async deleteGroup(tenantId: number, group: string): Promise<void> {
    await request<unknown>('DELETE', `${base(tenantId)}/${group}`);
  },
};
